import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader, Card } from '@/components/Primitives';
import { Plus, Trash, FloppyDisk, ArrowLeft } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const NODE_TYPES = [
    { v: 'greeting', l: 'Uvítání (přehrání audio)' },
    { v: 'ivr_menu', l: 'IVR Menu (DTMF výběr)' },
    { v: 'ai_router', l: 'AI Virtuální Asistentka (rozpoznání hlasu)' },
    { v: 'extension', l: 'Přepojit na klapku' },
    { v: 'ring_group', l: 'Přepojit na Ring Group' },
    { v: 'external', l: 'Přepojit na externí číslo (GSM)' },
    { v: 'voicemail', l: 'Voicemail' },
    { v: 'hangup', l: 'Zavěsit' },
];

const newNode = (type) => ({
    id: 'n_' + Math.random().toString(36).slice(2, 10),
    type,
    name: '',
    greeting_audio_id: null,
    greeting_text: '',
    extension_id: null,
    ring_group_id: null,
    external_number: '',
    voicemail_extension_id: null,
    options: [],
    ai_provider: 'openai',
    ai_model: null,
    ai_mode: 'single_shot',
    ai_intents: [],
    ai_prompt: '',
    ai_language: 'cs',
    ai_max_listen_seconds: 5,
    timeout_seconds: 10,
    next_node_id: null,
    fallback_node_id: null,
});

const CallFlowEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [flow, setFlow] = useState(null);
    const [audios, setAudios] = useState([]);
    const [exts, setExts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [dids, setDids] = useState([]);
    const { toast } = useToast();

    const load = async () => {
        const [f, a, e, g, d] = await Promise.all([
            api.get(`/call-flows/${id}`),
            api.get('/media/audio'),
            api.get('/extensions'),
            api.get('/ring-groups'),
            api.get('/didww/dids'),
        ]);
        setFlow(f.data);
        setAudios(a.data);
        setExts(e.data);
        setGroups(g.data);
        setDids(d.data);
    };
    useEffect(() => { load(); }, [id]);

    if (!flow) return <div>Načítání…</div>;

    const addNode = (type) => {
        const node = newNode(type);
        setFlow({ ...flow, nodes: [...flow.nodes, node],
                  entry_node_id: flow.entry_node_id || node.id });
    };

    const updateNode = (nid, patch) => {
        setFlow({ ...flow, nodes: flow.nodes.map((n) => n.id === nid ? { ...n, ...patch } : n) });
    };

    const removeNode = (nid) => {
        setFlow({
            ...flow,
            nodes: flow.nodes.filter((n) => n.id !== nid),
            entry_node_id: flow.entry_node_id === nid ? null : flow.entry_node_id,
        });
    };

    const save = async () => {
        try {
            await api.put(`/call-flows/${id}`, flow);
            toast({ title: 'Uloženo' });
            load();
        } catch (e) {
            toast({ title: 'Chyba', description: e.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Call Flow Builder"
                title={flow.name}
                description={flow.description || 'Definujte uvítání, IVR menu a směrování.'}
                actions={
                    <div className="flex gap-2">
                        <button onClick={() => navigate('/call-flows')}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] border"
                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                            <ArrowLeft size={14} weight="bold" />Zpět
                        </button>
                        <button onClick={save}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                                style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                            <FloppyDisk size={14} weight="bold" />Uložit
                        </button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
                <Card title="Základ" className="lg:col-span-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Název</label>
                            <input value={flow.name}
                                   onChange={(e) => setFlow({ ...flow, name: e.target.value })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>DID</label>
                            <select value={flow.did_number_id || ''}
                                    onChange={(e) => setFlow({ ...flow, did_number_id: e.target.value || null })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                <option value="">— žádný —</option>
                                {dids.map((d) => <option key={d.id} value={d.id}>{d.number}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Entry node</label>
                            <select value={flow.entry_node_id || ''}
                                    onChange={(e) => setFlow({ ...flow, entry_node_id: e.target.value || null })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                <option value="">— žádný —</option>
                                {flow.nodes.map((n) => <option key={n.id} value={n.id}>{n.name || n.type}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
                                   style={{ color: 'var(--text-secondary)' }}>Fallback (GSM, SIP URI…)</label>
                            <input value={flow.fallback_destination || ''}
                                   onChange={(e) => setFlow({ ...flow, fallback_destination: e.target.value })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                    </div>
                </Card>
                <Card title="Pracovní doba">
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!flow.working_hours?.enabled}
                               onChange={(e) => setFlow({ ...flow, working_hours: { ...flow.working_hours, enabled: e.target.checked } })} />
                        Aktivovat
                    </label>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <input value={flow.working_hours?.start || '09:00'}
                               onChange={(e) => setFlow({ ...flow, working_hours: { ...flow.working_hours, start: e.target.value } })}
                               className="px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                               style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        <input value={flow.working_hours?.end || '17:00'}
                               onChange={(e) => setFlow({ ...flow, working_hours: { ...flow.working_hours, end: e.target.value } })}
                               className="px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                               style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                    </div>
                </Card>
                <Card title="Přidat uzel">
                    <div className="space-y-1">
                        {NODE_TYPES.map((t) => (
                            <button key={t.v} onClick={() => addNode(t.v)}
                                    className="w-full text-left px-2 py-1.5 text-xs border hover:bg-[var(--bg-surface-hover)] flex items-center justify-between"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                    data-testid={`add-node-${t.v}`}>
                                <span>{t.l}</span>
                                <Plus size={12} weight="bold" />
                            </button>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="space-y-3">
                {flow.nodes.length === 0 && (
                    <div className="border p-8 text-center text-sm font-mono"
                         style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', borderRadius: 2 }}>
                        Zatím žádné uzly. Přidejte první uzel z pravého panelu.
                    </div>
                )}
                {flow.nodes.map((n, idx) => (
                    <div key={n.id} className="flow-node">
                        <div className="flow-node-header flex justify-between">
                            <span>{idx + 1} · {NODE_TYPES.find((t) => t.v === n.type)?.l || n.type}
                                {flow.entry_node_id === n.id && <span className="ml-2 text-[var(--brand)]">[ENTRY]</span>}
                            </span>
                            <button onClick={() => removeNode(n.id)}>
                                <Trash size={12} />
                            </button>
                        </div>
                        <div className="p-3 grid grid-cols-2 gap-3 text-xs">
                            <div className="col-span-2">
                                <label className="text-[10px] uppercase font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Název uzlu</label>
                                <input value={n.name || ''}
                                       onChange={(e) => updateNode(n.id, { name: e.target.value })}
                                       className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                       style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                            </div>
                            {n.type === 'greeting' && (
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>Audio asset</label>
                                    <select value={n.greeting_audio_id || ''}
                                            onChange={(e) => updateNode(n.id, { greeting_audio_id: e.target.value || null })}
                                            className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        <option value="">— vyberte audio —</option>
                                        {audios.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.source})</option>)}
                                    </select>
                                </div>
                            )}
                            {n.type === 'ivr_menu' && (
                                <div className="col-span-2 space-y-2">
                                    <label className="text-[10px] uppercase font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>Audio + DTMF volby</label>
                                    <select value={n.greeting_audio_id || ''}
                                            onChange={(e) => updateNode(n.id, { greeting_audio_id: e.target.value || null })}
                                            className="w-full px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        <option value="">— vyberte uvítací audio —</option>
                                        {audios.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                    {(n.options || []).map((opt, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                            <input value={opt.digit} maxLength={1}
                                                   onChange={(e) => {
                                                       const copy = [...n.options];
                                                       copy[i] = { ...opt, digit: e.target.value };
                                                       updateNode(n.id, { options: copy });
                                                   }}
                                                   className="col-span-1 px-2 py-1 bg-[var(--bg-app)] border text-xs font-mono text-center"
                                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                            <input value={opt.label || ''} placeholder="Popisek"
                                                   onChange={(e) => {
                                                       const copy = [...n.options];
                                                       copy[i] = { ...opt, label: e.target.value };
                                                       updateNode(n.id, { options: copy });
                                                   }}
                                                   className="col-span-4 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                            <select value={opt.target_node_id || ''}
                                                    onChange={(e) => {
                                                        const copy = [...n.options];
                                                        copy[i] = { ...opt, target_node_id: e.target.value };
                                                        updateNode(n.id, { options: copy });
                                                    }}
                                                    className="col-span-6 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                <option value="">— cílový uzel —</option>
                                                {flow.nodes.filter((x) => x.id !== n.id).map((x) =>
                                                    <option key={x.id} value={x.id}>{x.name || x.type}</option>
                                                )}
                                            </select>
                                            <button onClick={() => updateNode(n.id, { options: n.options.filter((_, j) => j !== i) })}
                                                    className="col-span-1 text-red-400">
                                                <Trash size={12} />
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button"
                                            onClick={() => updateNode(n.id, { options: [...(n.options || []), { digit: '', label: '', target_node_id: null }] })}
                                            className="text-xs px-2 py-1 border"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        + DTMF volba
                                    </button>
                                </div>
                            )}
                            {n.type === 'extension' && (
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>Klapka</label>
                                    <select value={n.extension_id || ''}
                                            onChange={(e) => updateNode(n.id, { extension_id: e.target.value || null })}
                                            className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        <option value="">—</option>
                                        {exts.map((e) => <option key={e.id} value={e.id}>{e.extension} · {e.display_name}</option>)}
                                    </select>
                                </div>
                            )}
                            {n.type === 'ring_group' && (
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>Ring Group</label>
                                    <select value={n.ring_group_id || ''}
                                            onChange={(e) => updateNode(n.id, { ring_group_id: e.target.value || null })}
                                            className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        <option value="">—</option>
                                        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                    </select>
                                </div>
                            )}
                            {n.type === 'external' && (
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>Externí číslo (např. GSM +420...)</label>
                                    <input value={n.external_number || ''}
                                           onChange={(e) => updateNode(n.id, { external_number: e.target.value })}
                                           className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs font-mono"
                                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                </div>
                            )}
                            {n.type === 'voicemail' && (
                                <div className="col-span-2">
                                    <label className="text-[10px] uppercase font-bold"
                                           style={{ color: 'var(--text-secondary)' }}>Voicemail pro klapku</label>
                                    <select value={n.voicemail_extension_id || ''}
                                            onChange={(e) => updateNode(n.id, { voicemail_extension_id: e.target.value || null })}
                                            className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        <option value="">—</option>
                                        {exts.map((e) => <option key={e.id} value={e.id}>{e.extension}</option>)}
                                    </select>
                                </div>
                            )}
                            {n.type === 'ai_router' && (
                                <div className="col-span-2 space-y-3 p-3"
                                     style={{ background: 'rgba(0,102,255,0.05)', borderRadius: 2 }}>
                                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold"
                                         style={{ color: 'var(--brand)' }}>
                                        AI Virtuální Asistentka
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className="text-[10px] uppercase font-bold"
                                                   style={{ color: 'var(--text-secondary)' }}>Mód</label>
                                            <select value={n.ai_mode || 'single_shot'}
                                                    onChange={(e) => updateNode(n.id, { ai_mode: e.target.value })}
                                                    data-testid={`ai-mode-${n.id}`}
                                                    className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                <option value="single_shot">Volné rozpoznání (a)</option>
                                                <option value="hybrid">Hybridní DTMF + hlas (b)</option>
                                                <option value="conversational">Konverzační dialog (c)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase font-bold"
                                                   style={{ color: 'var(--text-secondary)' }}>Provider</label>
                                            <select value={n.ai_provider || 'openai'}
                                                    onChange={(e) => updateNode(n.id, { ai_provider: e.target.value, ai_model: null })}
                                                    data-testid={`ai-provider-${n.id}`}
                                                    className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                <option value="openai">OpenAI</option>
                                                <option value="anthropic">Anthropic Claude</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase font-bold"
                                                   style={{ color: 'var(--text-secondary)' }}>Model</label>
                                            <select value={n.ai_model || ''}
                                                    onChange={(e) => updateNode(n.id, { ai_model: e.target.value || null })}
                                                    data-testid={`ai-model-${n.id}`}
                                                    className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                <option value="">Default</option>
                                                {(n.ai_provider === 'anthropic'
                                                    ? ['claude-sonnet-4-5-20250929', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001']
                                                    : ['gpt-5.2', 'gpt-5.4', 'gpt-5-mini', 'gpt-4.1']
                                                ).map((m) => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] uppercase font-bold"
                                               style={{ color: 'var(--text-secondary)' }}>Uvítací audio (případně i instrukce)</label>
                                        <select value={n.greeting_audio_id || ''}
                                                onChange={(e) => updateNode(n.id, { greeting_audio_id: e.target.value || null })}
                                                className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                            <option value="">— vyberte audio —</option>
                                            {audios.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-[10px] uppercase font-bold"
                                               style={{ color: 'var(--text-secondary)' }}>Dodatečné instrukce pro AI (volitelné)</label>
                                        <textarea
                                            rows={2}
                                            value={n.ai_prompt || ''}
                                            onChange={(e) => updateNode(n.id, { ai_prompt: e.target.value })}
                                            placeholder="Např. 'Pokud volající mluví o Solar Green, klasifikuj přesně.'"
                                            className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                        />
                                    </div>

                                    <div>
                                        <label className="text-[10px] uppercase font-bold"
                                               style={{ color: 'var(--text-secondary)' }}>Intenty + cílové uzly</label>
                                        <div className="mt-1 space-y-2">
                                            {(n.ai_intents || []).map((it, i) => (
                                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                                    {n.ai_mode === 'hybrid' && (
                                                        <input
                                                            value={it.dtmf_digit || ''}
                                                            placeholder="DTMF"
                                                            maxLength={1}
                                                            onChange={(e) => {
                                                                const c = [...n.ai_intents];
                                                                c[i] = { ...it, dtmf_digit: e.target.value };
                                                                updateNode(n.id, { ai_intents: c });
                                                            }}
                                                            className="col-span-1 px-1 py-1 bg-[var(--bg-app)] border text-xs font-mono text-center"
                                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                                                        />
                                                    )}
                                                    <input value={it.name}
                                                           placeholder="Název intentu"
                                                           onChange={(e) => {
                                                               const c = [...n.ai_intents];
                                                               c[i] = { ...it, name: e.target.value };
                                                               updateNode(n.id, { ai_intents: c });
                                                           }}
                                                           className={`${n.ai_mode === 'hybrid' ? 'col-span-3' : 'col-span-3'} px-2 py-1 bg-[var(--bg-app)] border text-xs`}
                                                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                                    <input value={it.description || ''}
                                                           placeholder="Popis (např. 'obchodní dotaz, ceny')"
                                                           onChange={(e) => {
                                                               const c = [...n.ai_intents];
                                                               c[i] = { ...it, description: e.target.value };
                                                               updateNode(n.id, { ai_intents: c });
                                                           }}
                                                           className={`${n.ai_mode === 'hybrid' ? 'col-span-4' : 'col-span-5'} px-2 py-1 bg-[var(--bg-app)] border text-xs`}
                                                           style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                                    <select value={it.target_node_id || ''}
                                                            onChange={(e) => {
                                                                const c = [...n.ai_intents];
                                                                c[i] = { ...it, target_node_id: e.target.value };
                                                                updateNode(n.id, { ai_intents: c });
                                                            }}
                                                            className="col-span-3 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                        <option value="">— cíl —</option>
                                                        {flow.nodes.filter((x) => x.id !== n.id).map((x) =>
                                                            <option key={x.id} value={x.id}>{x.name || x.type}</option>
                                                        )}
                                                    </select>
                                                    <button onClick={() => updateNode(n.id, { ai_intents: n.ai_intents.filter((_, j) => j !== i) })}
                                                            className="col-span-1 text-red-400">
                                                        <Trash size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button type="button"
                                                    data-testid={`ai-add-intent-${n.id}`}
                                                    onClick={() => updateNode(n.id, { ai_intents: [...(n.ai_intents || []), { name: '', description: '', target_node_id: null }] })}
                                                    className="text-xs px-2 py-1 border"
                                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                + Intent
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[10px] uppercase font-bold"
                                                   style={{ color: 'var(--text-secondary)' }}>Max. doba poslechu (s)</label>
                                            <input type="number" value={n.ai_max_listen_seconds || 5}
                                                   onChange={(e) => updateNode(n.id, { ai_max_listen_seconds: parseInt(e.target.value, 10) })}
                                                   className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs font-mono"
                                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] uppercase font-bold"
                                                   style={{ color: 'var(--text-secondary)' }}>Jazyk</label>
                                            <select value={n.ai_language || 'cs'}
                                                    onChange={(e) => updateNode(n.id, { ai_language: e.target.value })}
                                                    className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                                <option value="cs">Čeština</option>
                                                <option value="en">English</option>
                                                <option value="sk">Slovenčina</option>
                                            </select>
                                        </div>
                                    </div>

                                    <AiTester node={n} />
                                </div>
                            )}
                            <div>
                                <label className="text-[10px] uppercase font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Timeout (s)</label>
                                <input type="number" value={n.timeout_seconds || 10}
                                       onChange={(e) => updateNode(n.id, { timeout_seconds: parseInt(e.target.value, 10) })}
                                       className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs font-mono"
                                       style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase font-bold"
                                       style={{ color: 'var(--text-secondary)' }}>Další uzel (next)</label>
                                <select value={n.next_node_id || ''}
                                        onChange={(e) => updateNode(n.id, { next_node_id: e.target.value || null })}
                                        className="w-full mt-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                                        style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                    <option value="">—</option>
                                    {flow.nodes.filter((x) => x.id !== n.id).map((x) =>
                                        <option key={x.id} value={x.id}>{x.name || x.type}</option>
                                    )}
                                </select>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default CallFlowEditor;

const AiTester = ({ node }) => {
    const [text, setText] = React.useState('Dobry den, chtel bych se zeptat na ceny solarnich panelu');
    const [busy, setBusy] = React.useState(false);
    const [result, setResult] = React.useState(null);

    const test = async () => {
        if (!node.ai_intents || node.ai_intents.length === 0) {
            setResult({ error: 'Nejdřív přidejte intenty.' });
            return;
        }
        setBusy(true);
        try {
            const r = await api.post('/ai/classify-text', {
                text,
                intents: node.ai_intents.map((i) => ({ name: i.name, description: i.description, aliases: i.aliases || [] })),
                provider: node.ai_provider || 'openai',
                model: node.ai_model,
                language: node.ai_language || 'cs',
                extra_system_prompt: node.ai_prompt || null,
            });
            setResult(r.data);
        } catch (e) {
            setResult({ error: e.response?.data?.detail || 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 border-t pt-3" style={{ borderColor: 'var(--border-default)' }}>
            <div className="text-[10px] uppercase tracking-[0.1em] font-bold mb-2"
                 style={{ color: 'var(--text-secondary)' }}>Test rozpoznání</div>
            <div className="flex gap-2">
                <input value={text} onChange={(e) => setText(e.target.value)}
                       data-testid={`ai-test-input-${node.id}`}
                       placeholder="Co řekl volající?"
                       className="flex-1 px-2 py-1 bg-[var(--bg-app)] border text-xs"
                       style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                <button onClick={test} disabled={busy}
                        data-testid={`ai-test-run-${node.id}`}
                        className="px-3 py-1 text-xs font-bold uppercase"
                        style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                    {busy ? '…' : 'Test'}
                </button>
            </div>
            {result && (
                <div className="mt-2 px-2 py-2 font-mono text-[11px]"
                     style={{ background: '#06080A', borderRadius: 2 }}
                     data-testid={`ai-test-result-${node.id}`}>
                    {result.error ? (
                        <span className="text-red-400">{result.error}</span>
                    ) : (
                        <>
                            <div>Intent: <span style={{ color: 'var(--brand)' }}>{result.intent}</span> (conf {result.confidence?.toFixed(2)})</div>
                            <div style={{ color: 'var(--text-secondary)' }}>{result.reasoning}</div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
